/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import {
  Response,
  Subtype,
  CourseLevel,
  SuccessResponse,
} from '../../types/ClassTable.type';
import { apiGet, apiPost } from '../../utils/apiClient';
import { notifyAPIClient } from '../../utils/toast';
import { subtleFieldBg } from '../../utils/rowHighlight';
import { getDefaultClassCapacity } from '../../utils/classCapacity';
import { z } from 'zod';

interface ClassInputData {
  name: string;
  classDate?: string;
  startTime?: string;
  endTime?: string;
  level?: string;
  subtypeId: number;
  maxStudents: number;
  note?: string;
}

const ClassInputSchema = z.object({
  name: z.string().min(1, 'Class name is required'),
  classDate: z
    .string({
      required_error: 'Please enter class date',
    })
    .date('Please enter a valid date'),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  level: z.string().optional(),
  subtypeId: z.number().min(1, 'Subtype is required'),
  maxStudents: z.number().int().min(1, 'Maximum students must be at least 1'),
  note: z.string().optional(),
});

type CreateClassDialogProps = {
  open: boolean;
  handleClose: () => void;
  fetchClasses: () => void;
  subtypes?: Subtype[];
};

const CreateClassDialog = ({
  open,
  handleClose,
  fetchClasses,
  subtypes,
}: CreateClassDialogProps) => {
  const [classData, setClassData] = useState<ClassInputData>({
    name: '',
    classDate: new Date().toISOString().split('T')[0],
    startTime: '07:00',
    endTime: '08:00',
    level: '',
    subtypeId: subtypes && subtypes.length > 0 ? subtypes[0].id : 0,
    maxStudents: 4,
    note: '',
  });
  const [levelOptions, setLevelOptions] = useState<CourseLevel[]>([]);

  const getSelectedSubtypeName = () => {
    const selectedSubtype = subtypes?.find(
      (st) => st.id === classData.subtypeId,
    );
    return selectedSubtype?.name || '';
  };

  const fetchCourseLevels = async (subtypeId: number) => {
    if (subtypeId === 0) {
      setLevelOptions([]);
      return;
    }
    // Find the selected subtype and get its first subsubtype ID
    const selectedSubtype = subtypes?.find((st) => st.id === subtypeId);
    if (
      !selectedSubtype ||
      !selectedSubtype.subsubtypes ||
      selectedSubtype.subsubtypes.length === 0
    ) {
      setLevelOptions([]);
      return;
    }
    const subSubtypeId = selectedSubtype.subsubtypes[0].id;
    const res = await apiGet<Response<CourseLevel>>(
      `/course/get-levels/${subSubtypeId}`,
    );
    setLevelOptions(res.data);
  };

  const getDayAbbreviation = (date: string): string => {
    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const d = new Date(date);
    return dayNames[d.getDay()];
  };

  const handleDataChange = <T extends keyof ClassInputData>(
    key: T,
    value: ClassInputData[T],
  ) => {
    setClassData((prev) => {
      const updated = {
        ...prev,
        [key]: value,
      };

      if (key === 'level' || key === 'subtypeId') {
        const selectedSubtype = subtypes?.find(
          (subtype) => subtype.id === updated.subtypeId,
        );
        updated.maxStudents = getDefaultClassCapacity(
          selectedSubtype?.name,
          updated.level,
        );
      }

      // Auto-update class name when level, startTime, endTime, classDate, or subtypeId changes
      if (
        key === 'level' ||
        key === 'startTime' ||
        key === 'endTime' ||
        key === 'classDate' ||
        key === 'subtypeId'
      ) {
        const subtypeName =
          key === 'subtypeId'
            ? subtypes?.find((st) => st.id === (value as number))?.name || ''
            : getSelectedSubtypeName();
        const dayAbbr = getDayAbbreviation(updated.classDate || '');

        const namePrefix =
          updated.level && subtypeName
            ? `${subtypeName} ${updated.level}`
            : subtypeName;

        updated.name = namePrefix
          ? `[${namePrefix}] - ${dayAbbr} ${updated.startTime}-${updated.endTime}`
          : `${dayAbbr} ${updated.startTime}-${updated.endTime}`;

        // Fetch levels when subtypeId changes
        if (key === 'subtypeId') {
          fetchCourseLevels(value as number);
        }
      }

      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!classData.name || !classData.classDate) {
      notifyAPIClient('Please fill in all required fields.');
      return;
    }

    try {
      const validatedClass = ClassInputSchema.parse(classData);
      console.log('Validated class:', validatedClass);

      await apiPost<SuccessResponse>('/class/create', validatedClass);

      notifyAPIClient('Class created successfully');
      fetchClasses();
      handleCloseDialog();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: any = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof typeof fieldErrors;
          fieldErrors[field] = err.message;
        });
        console.error('Field errors:', fieldErrors);
        notifyAPIClient('Please input required class fields correctly.');
      } else {
        console.error(error);
        notifyAPIClient(
          'An unexpected error occurred while processing your request.',
        );
      }
    }
  };

  const handleCloseDialog = () => {
    setClassData({
      name: '',
      classDate: new Date().toISOString().split('T')[0],
      startTime: '07:00',
      endTime: '08:00',
      level: '',
      subtypeId: subtypes && subtypes.length > 0 ? subtypes[0].id : 0,
      maxStudents: 4,
      note: '',
    });
    setLevelOptions([]);
    handleClose();
  };

  useEffect(() => {
    if (open) {
      if (subtypes && subtypes.length > 0) {
        const initialSubtypeId = subtypes[0].id;
        const subtypeName = subtypes[0].name;
        const dayAbbr = getDayAbbreviation(
          new Date().toISOString().split('T')[0],
        );
        const autoName = `[${subtypeName}] - ${dayAbbr} 07:00-08:00`;

        setClassData((prev) => ({
          ...prev,
          subtypeId: initialSubtypeId,
          maxStudents: getDefaultClassCapacity(subtypeName, prev.level),
          name: autoName,
        }));

        fetchCourseLevels(initialSubtypeId);
      }
    }
  }, [open, subtypes]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleCloseDialog}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          color: '#fff',
        }}
      >
        <Typography variant="h6" fontWeight="700" sx={{ color: '#fff' }}>
          Create New Class
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, px: 3, pb: 2 }}>
        <Box display="flex" flexDirection="column" gap={2.5} mt={3}>
          <TextField
            className="required-field"
            label="Class Name"
            type="text"
            value={classData.name}
            onChange={(e) => handleDataChange('name', e.target.value)}
            fullWidth
            placeholder="Auto-generated based on selections"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: subtleFieldBg,
              },
            }}
          />

          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              className="required-field"
              label="Class Date"
              type="date"
              value={classData.classDate}
              onChange={(e) => handleDataChange('classDate', e.target.value)}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />

            <TextField
              select
              label="Class Level"
              value={classData.level}
              onChange={(e) => handleDataChange('level', e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            >
              {levelOptions.map((lvl, idx) => (
                <MenuItem key={idx} value={lvl.levelName}>
                  {lvl.levelName}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            label="Maximum Students"
            type="number"
            value={classData.maxStudents}
            onChange={(e) =>
              handleDataChange(
                'maxStudents',
                Math.max(1, Number(e.target.value) || 1),
              )
            }
            fullWidth
            inputProps={{ min: 1, step: 1 }}
            helperText="Default is based on class type and level. Admin can customize it."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />

          <TextField
            select
            className="required-field"
            label="Class Type"
            value={classData.subtypeId}
            onChange={(e) =>
              handleDataChange('subtypeId', Number(e.target.value))
            }
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          >
            {subtypes?.map((subtype) => (
              <MenuItem key={subtype.id} value={subtype.id}>
                {subtype.name}
              </MenuItem>
            ))}
          </TextField>

          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Start Time"
              type="time"
              value={classData.startTime}
              onChange={(e) => handleDataChange('startTime', e.target.value)}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
            <TextField
              label="End Time"
              type="time"
              value={classData.endTime}
              onChange={(e) => handleDataChange('endTime', e.target.value)}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
          </Box>

          <TextField
            label="Note (Optional)"
            type="text"
            value={classData.note}
            onChange={(e) => handleDataChange('note', e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Add any additional notes"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          px: 3,
          py: 2.5,
          background: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Button
          onClick={handleCloseDialog}
          variant="outlined"
          sx={(theme) => ({
            borderRadius: '10px',
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary,
            '&:hover': {
              borderColor: theme.palette.text.secondary,
              backgroundColor: theme.palette.action.hover,
            },
          })}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          variant="contained"
          sx={{
            color: 'white',
            borderRadius: '10px',
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Create Class
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateClassDialog;
